import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Drawer, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import CustomSelect from '../../../../common/components/customized/customselect/CustomSelect';
import CustomButton from '../../../../common/components/buttons/CustomButton';
import CustomTextField from '../../../../common/components/customized/customtextfield/CustomTextField';
import CustomDateCalendar from '../../../../common/components/customized/customcalendar/CustomCalendar';
import dayjs from 'dayjs';
import {
  capitalizeFirstLetter,
  convertToEpoch2
} from '../../../../utils/commonFunctions';
import { useAppDispatch, useAppSelector } from '../../../../app/redux/hooks';
import {
  addMaintenance,
  getVehicleNumber,
  getVehicleType
} from '../../redux/reducer/truckManagementSlices/maintenanceSlice';
import CustomRadioButton from '../../../../common/components/customized/customradiobutton/CustomRadioButton';
import './AddMaintenance.scss';
import {
  getVehicleMakeListData,
  getVehicleMakeTypeList,
  getVehiclePartsList
} from '../../redux/reducer/truckManagementSlices/vehicleConfigManagerSlice';

const maintenanceSchema: any = yup.object().shape({
  vehicleType: yup
    .string()
    .required('Select Vehicle Type')
    .typeError('Vehicle Type must be a string'),
  vehicleNumber: yup
    .string()
    .required('Select Vehicle Number')
    .typeError('Vehicle Number must be a string'),
  equipmentMaintenance: yup
    .string()
    .required('Select Equipment Maintenance')
    .typeError('Equipment Maintenance must be a string'),
  make: yup.string().required('Select make').typeError('Make must be a string'),
  type: yup.string().required('Select Type').typeError('Type must be a string'),
  maintenanceDate: yup
    .string()
    .required('Select Maintenance Date')
    .typeError('Maintenance Date must be a string'),
  dueCriteria: yup.string().trim().required(),
  serviceInterval: yup.string().when('dueCriteria', {
    is: (value: any) => {
      return value === 'Time' || value === 'Both';
    },
    then: () =>
      yup
        .string()
        .trim()
        .required('Enter the Interval')
        .typeError('Service Interval must be a string'),
    otherwise: () => yup.string().trim().notRequired()
  }),
  duration: yup.string().when('dueCriteria', {
    is: (value: any) => {
      return value === 'Time' || value === 'Both';
    },
    then: () =>
      yup.string().required('Enter the Duration').typeError('Duration must be a string'),
    otherwise: () => yup.string().trim().notRequired()
  }),
  nextDueDate: yup.string().when('dueCriteria', {
    is: (value: any) => {
      return value === 'Time' || value === 'Both';
    },
    then: () =>
      yup
        .string()
        .required('Select Next Due Date')
        .typeError('Next Due Date must be a string'),
    otherwise: () => yup.string().trim().notRequired()
  }),
  currentOdometer: yup
    .string()
    .trim()
    .when('dueCriteria', {
      is: (value: any) => {
        return value === 'Miles' || value === 'Both';
      },
      then: () =>
        yup
          .number()
          .required('Enter the Current Odometer')
          .typeError('Current Odometer must be a number'),
      otherwise: () => yup.string().trim().notRequired()
    }),
  dueOdometerDistance: yup
    .string()
    .trim()
    .when('dueCriteria', {
      is: (value: any) => {
        return value === 'Miles' || value === 'Both';
      },
      then: () =>
        yup
          .number()
          .required('Select Due Odometer Distance')
          .typeError('Next Due Odometer Distance be a number'),
      otherwise: () => yup.string().trim().notRequired()
    })
});

interface AddMaintenance {
  open: boolean;
  setOpen: any;
  roleType: string;
  filteredRow: any;
  setFilteredRow: any;
}

interface Options {
  id: string;
  label: string;
}

interface TextField {
  id: string;
  label: string;
  field:
    | 'vehicleType'
    | 'vehicleNumber'
    | 'equipmentMaintenance'
    | 'make'
    | 'type'
    | 'maintenanceDate'
    | 'dueCriteria'
    | 'serviceInterval'
    | 'duration'
    | 'nextDueDate'
    | 'currentOdometer'
    | 'dueOdometerDistance';
  placeholder: string;
  options?: any;
}

const AddMaintenance = ({
  open,
  setOpen,
  roleType,
  filteredRow,
  setFilteredRow
}: AddMaintenance) => {
  const [dueCriteria, setDueCriteria] = useState('Time');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [maintenance, setMaintenance] = useState<any>(null);
  const [vehicleType, setVehicleType] = useState('');
  const [partDrop, setPartDrop] = useState('');
  const [intervalTime, setIntervalTime] = useState<number | any>(null);
  const [isDuration, setIsDuration] = useState<string>('');

  const { isLoading } = useAppSelector(state => state.addMaintenance);
  const { data: vehicleTypeData, isLoading: vehicleTypeLoading } = useAppSelector(
    state => state.getVehicleType
  );
  const { vehicleNumberData, isLoading: vehicleLoading } = useAppSelector(
    state => state.getVehicleNumber
  );
  const {
    data: { getPartsList, getMakeList, getTypeList },
    isLoading: { isPartsLoading, isMakeLoading, isTypeLoading }
  } = useAppSelector(state => state?.vehicleConfigData);
  const dispatch = useAppDispatch();

  const { control, handleSubmit, setValue, clearErrors, reset, getValues } = useForm({
    resolver: yupResolver(maintenanceSchema)
  });

  const vehicleTypeDrop =
    vehicleTypeData?.vehicleType?.length > 0
      ? vehicleTypeData.vehicleType.map((value: string, index: number) => ({
          id: value,
          label: capitalizeFirstLetter(value)
        }))
      : [];

  const vehicleNumberDropData =
    vehicleNumberData?.vehicleNumber?.length > 0
      ? vehicleNumberData.vehicleNumber.map((value: string, index: number) => ({
          id: value,
          label: value.toUpperCase()
        }))
      : [];

  const partDropDown =
    getPartsList?.data && getPartsList?.data?.length > 0
      ? getPartsList?.data?.map((value: any) => ({
          id: value,
          label: capitalizeFirstLetter(value)
        }))
      : [];

  const makeDropDown =
    getMakeList?.data && getMakeList?.data?.length > 0
      ? getMakeList?.data?.map((value: any) => ({
          id: value,
          label: capitalizeFirstLetter(value)
        }))
      : [];

  const typeDropDown =
    getTypeList?.data && getTypeList?.data?.length > 0
      ? getTypeList?.data?.map((value: any) => ({
          id: value,
          label: capitalizeFirstLetter(value)
        }))
      : [];

  const duration = [
    { id: 'days', label: 'Days' },
    { id: 'months', label: 'Months' },
    { id: 'year', label: 'Year' }
  ];

  const textField: TextField[] = [
    {
      id: '1',
      label: 'Vehicle Type',
      field: 'vehicleType',
      placeholder: 'Vehicle Type',
      options: vehicleTypeDrop ?? []
    },
    {
      id: '2',
      label: 'Vehicle Number',
      field: 'vehicleNumber',
      placeholder: 'Vehicle Number',
      options: vehicleNumberDropData ?? []
    },
    {
      id: '3',
      label: 'Equipment Maintenance',
      field: 'equipmentMaintenance',
      placeholder: 'Equipment Maintenance',
      options: partDropDown ?? []
    },
    {
      id: '4',
      label: 'Make',
      field: 'make',
      placeholder: 'Make',
      options: makeDropDown ?? []
    },
    {
      id: '5',
      label: 'Type',
      field: 'type',
      placeholder: 'Type',
      options: typeDropDown ?? []
    },
    {
      id: '6',
      label: 'Maintenance Date',
      field: 'maintenanceDate',
      placeholder: 'Maintenance Date'
    },
    {
      id: '7',
      label: 'Due Criteria',
      field: 'dueCriteria',
      placeholder: 'Due Criteria'
    },
    {
      id: '8',
      label: 'Service Interval',
      field: 'serviceInterval',
      placeholder: 'Service Interval'
    },
    {
      id: '9',
      label: 'Duration',
      field: 'duration',
      placeholder: 'Duration',
      options: duration ?? []
    },
    {
      id: '10',
      label: 'Next Due Date',
      field: 'nextDueDate',
      placeholder: 'Next Due Date'
    },
    {
      id: '11',
      label: 'Current Odometer (km)',
      field: 'currentOdometer',
      placeholder: 'Current Odometer'
    },
    {
      id: '12',
      label: 'Due Odometer Distance (km)',
      field: 'dueOdometerDistance',
      placeholder: 'Due Odometer Distance'
    }
  ];

  const onSubmit = async (data: any) => {
    const action1 = await dispatch(
      addMaintenance({
        currentOdometer: data.currentOdometer,
        dueCriteria: data.dueCriteria,
        dueOdometerDistance: data.dueOdometerDistance,
        intervalType: data.duration ? data.duration : 'days',
        maintenanceDate: data.maintenanceDate,
        make: data.make,
        nextDueDate: data.nextDueDate,
        part: data.equipmentMaintenance,
        serviceInterval: data.serviceInterval,
        type: data.type,
        vehicleNumber: data.vehicleNumber,
        vehicleType: data.vehicleType
      })
    );
    if (action1.type === addMaintenance.fulfilled.type) {
      setOpen(false);
    }
  };

  const handleDueCriteriaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDueCriteria(event.target.value);
    setValue('dueCriteria', event.target.value);
    clearErrors();
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const handleDurationChanges = (interval: number, duration: string) => {
    let dueDate = new Date(maintenance);
    setIntervalTime(interval);
    if (duration === 'days') {
      dueDate.setDate(Number(maintenance.getDate()) + Number(interval));
      dueDate.setHours(0, 0, 0, 0);
      setValue('nextDueDate', String(convertToEpoch2(dueDate)));
    } else if (duration === 'months') {
      dueDate.setMonth(Number(maintenance.getMonth() + Number(interval)));
      setValue('nextDueDate', String(convertToEpoch2(dueDate)));
    } else if (duration === 'year') {
      dueDate.setFullYear(Number(maintenance.getFullYear() + Number(interval)));
      setValue('nextDueDate', String(convertToEpoch2(dueDate)));
    }
  };

  useEffect(() => {
    setValue('dueCriteria', 'Time');
  }, []);

  useEffect(() => {
    dispatch(getVehicleType());
  }, [dueCriteria]);

  return (
    <Box p={2}>
      <Drawer
        anchor='right'
        open={open}
        sx={{ zIndex: 0 }}
        onClose={() => {
          setOpen(false);
          reset();
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px !important'
          }}
          className='animate__animated animate__slideInUp'
        >
          <Typography sx={{ fontWeight: 700 }}>{roleType}</Typography>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 15 }}
            className='maintenance-container'
          >
            <Box component={'form'} onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={dueCriteria === 'Miles' ? 1 : 1}>
                {textField?.map(item => (
                  <Grid
                    item
                    key={item.id}
                    xs={item.id === '8' || item.id === '9' ? 6 : 12}
                    mt={
                      item.id === '9'
                        ? 1
                        : item?.id === '11' && dueCriteria === 'Miles'
                        ? -3
                        : 0
                    }
                    className='maintenance-item'
                  >
                    {item?.id === '6' ? (
                      <CustomDateCalendar
                        id={item.id}
                        name={item.field}
                        className='calendar'
                        control={control}
                        disableFuture={false}
                        disablePast={true}
                        placeholder={item.placeholder}
                        label={item.label}
                        value={
                          filteredRow
                            ? dayjs.unix(
                                item.id === '6' && filteredRow?.maintenanceDate !== null
                                  ? filteredRow?.maintenanceDate
                                  : filteredRow?.maintenanceDate
                              )
                            : null
                        }
                        onDateChange={(date: any) => {
                          const epoc = convertToEpoch2(date?.$d);
                          setMaintenance(date?.$d);
                          setValue('maintenanceDate', epoc !== null ? String(epoc) : '');
                          clearErrors('maintenanceDate');
                          setValue('serviceInterval', '');
                          setValue('duration', '');
                          setValue('nextDueDate', '');
                        }}
                      />
                    ) : item?.id === '1' ||
                      item?.id === '2' ||
                      item.id === '3' ||
                      item?.id === '4' ||
                      item?.id === '5' ? (
                      <CustomSelect
                        key={item.id}
                        id='select-vehicle'
                        control={control}
                        label={item.label}
                        name={item.field}
                        options={item.options}
                        placeholder={item.placeholder}
                        loading={
                          item?.id === '1'
                            ? vehicleTypeLoading
                            : item?.id === '2'
                            ? vehicleLoading
                            : item?.id === '3'
                            ? isPartsLoading
                            : item?.id === '4'
                            ? isMakeLoading
                            : isTypeLoading
                        }
                        isDisabled={
                          item.id === '2'
                            ? !vehicleNumber
                            : item.id === '4'
                            ? !partDrop
                            : item.id === '5'
                            ? !vehicleType
                            : false
                        }
                        onChanges={(e: any, newValue: any) => {
                          if (item?.id === '1' && newValue) {
                            dispatch(getVehicleNumber(newValue?.id));
                            dispatch(
                              getVehiclePartsList({ vehicleTypeData: newValue?.id })
                            );
                            setVehicleNumber(newValue?.id);
                          }
                          if (item?.id === '3' && newValue) {
                            // dispatch(makeDropdown(newValue?.id));
                            dispatch(
                              getVehicleMakeListData({
                                vehicleTypeData: vehicleNumber,
                                partsData: newValue?.id
                              })
                            );
                            setPartDrop(newValue?.id);
                          }
                          if (item?.id === '4' && newValue) {
                            // dispatch(
                            //   typeDropdown({ part: partDrop, make: newValue?.id })
                            // );
                            dispatch(
                              getVehicleMakeTypeList({
                                vehicleTypeData: vehicleNumber,
                                partsData: partDrop,
                                makeData: newValue?.id
                              })
                            );
                            setVehicleType(newValue?.id);
                          }
                          if (newValue === null) {
                            if (item.id === '1') {
                              setValue('vehicleNumber', '');
                              setValue('vehicle', '');
                              setVehicleNumber('');
                            }
                            if (item.id === '3') {
                              setValue('make', '');
                              setValue('type', '');
                              setVehicleType('');
                              setPartDrop('');
                            }
                            if (item.id === '4') {
                              setValue('type', '');
                              setVehicleType('');
                            }
                          }
                        }}
                      />
                    ) : item.id === '7' ? (
                      <CustomRadioButton
                        formlabel='Due Criteria'
                        name='dueCriteria'
                        radiobutton1='Time'
                        radiobutton2='Miles'
                        radiobutton3='Both'
                        value1='Time'
                        value2='Miles'
                        value3='Both'
                        control={control}
                        onChangeCallback={e => {
                          handleDueCriteriaChange(e);
                        }}
                      />
                    ) : null}

                    {(dueCriteria === 'Time' || dueCriteria === 'Both') &&
                      item.id === '8' && (
                        <CustomTextField
                          key={item.id}
                          control={control}
                          name={item.field}
                          placeholder={item.placeholder}
                          id={item.id}
                          label={item.label}
                          onChangeCallback={(e: any) => {
                            handleDurationChanges(e, isDuration);
                          }}
                        />
                      )}
                    {(dueCriteria === 'Time' || dueCriteria === 'Both') &&
                      item.id === '9' && (
                        <CustomSelect
                          key={item.id}
                          id='select-vehicle'
                          control={control}
                          label={item.label}
                          name={item.field}
                          options={item.options}
                          placeholder={item.placeholder}
                          onChanges={(
                            e: any,
                            newValue: { id: string; label: string }
                          ) => {
                            setIsDuration(newValue?.id);
                            handleDurationChanges(intervalTime, newValue?.id);
                          }}
                        />
                      )}
                    {(dueCriteria === 'Time' || dueCriteria === 'Both') &&
                      item.id === '10' && (
                        <CustomDateCalendar
                          id={item.id}
                          name={item.field}
                          className='calendar'
                          control={control}
                          disableFuture={false}
                          disablePast={true}
                          placeholder={item.placeholder}
                          label={item.label}
                          value={
                            filteredRow && filteredRow?.nextDueDate !== null
                              ? dayjs.unix(filteredRow?.nextDueDate)
                              : getValues('nextDueDate')
                              ? dayjs.unix(getValues('nextDueDate'))
                              : null
                          }
                          onDateChange={(date: any) => {
                            const epoc = date ? convertToEpoch2(date?.$d) : null;
                            setValue('nextDueDate', epoc !== null ? String(epoc) : '');
                            clearErrors('nextDueDate');

                            const maintenanceDate = getValues('maintenanceDate');
                            if (maintenanceDate && epoc) {
                              const diff = (epoc - maintenanceDate) / (24 * 60 * 60);
                              if (diff < 30) {
                                setValue('duration', 'days');
                                setIsDuration('days');
                                setValue('serviceInterval', String(diff));
                              } else if (diff < 365) {
                                setValue('duration', 'months');
                                const months = (diff / 30).toFixed(2);
                                setIsDuration('months');
                                setValue('serviceInterval', String(months));
                              } else {
                                setValue('duration', 'year');
                                setIsDuration('years');
                                const years = (diff / 365).toFixed(2);
                                setValue('serviceInterval', String(years));
                              }
                            }
                          }}
                        />
                      )}
                    {(dueCriteria === 'Miles' || dueCriteria === 'Both') &&
                      (item.id === '11' || item.id === '12') && (
                        <CustomTextField
                          key={item.id}
                          control={control}
                          name={item.field}
                          placeholder={item.placeholder}
                          id={item.id}
                          label={item.label}
                        />
                      )}
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ marginTop: '5%', display: 'flex', gap: '15px' }}>
                <CustomButton
                  className='saveChanges'
                  type='submit'
                  category={roleType}
                  loading={isLoading}
                />
                <CustomButton
                  className='cancel'
                  category='Cancel'
                  onClick={() => {
                    handleClose();
                    setFilteredRow();
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default AddMaintenance;
